import { useCallback, useState, useRef, startTransition } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import StatMetricCard from "@/components/analytics/StatMetricCard";
import DepartmentComparisonChart from "@/components/analytics/DepartmentComparisonChart";
import DepartmentTrendlineChart from "@/components/analytics/DepartmentTrendlineChart";
import OvertimeReasonsChart from "@/components/analytics/NewTrendlineChart";
import ApprovalStatusDonutChart from "@/components/analytics/ApprovalStatusDonutChart";
import OvertimeReasonDonutChart from "@/components/analytics/OvertimeReasonDonutChart";
import RejectionReasonDonutChart from "@/components/analytics/RejectionReasonDonutChart";
import EmployeeOvertimeTable from "@/components/analytics/EmployeeOvertimeTable";
import DashboardFilters, { DashboardFilters as FilterType } from "@/components/dashboard/DashboardFilters";
import { useConsolidatedAnalytics } from "@/hooks/useConsolidatedAnalytics";
import { TimeRange, UserRole, StatIcon } from "@/types/enums";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const Index = () => {
  // Filter state
  const [currentFilters, setCurrentFilters] = useState<FilterType>({
    timeRange: TimeRange.THIRTY_DAYS,
    departments: [],
    statuses: []
  });

  // timeoutRef is used to store the timer ID for deferred computation
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const navigate = useNavigate();

  // Use consolidated analytics hook - eliminates all redundant calculations
  const {
    filteredRequests: chartOvertimeRequests,
    dashboardStats,
    departmentData: filteredDepartmentData,
    availableDepartments,
    trends,
    isLoading,
    user
  } = useConsolidatedAnalytics(currentFilters);

  // Helper function to check if user can access pending requests
  const canAccessPendingRequests = user?.role === UserRole.PLANT_MANAGER || 
           user?.role === UserRole.HR || 
           user?.role === UserRole.SUPER_ADMIN || 
           user?.role === UserRole.SUPERINTENDENT || 
           user?.role === UserRole.DEPARTMENT_HEAD;

  // Handle click on pending requests card
  const handlePendingRequestsClick = () => {
    if (canAccessPendingRequests) {
      navigate('/pending-requests');
    }
  };

  // Simple filters check
  const hasActiveFilters = currentFilters.departments.length > 0 || 
                          currentFilters.statuses.length > 0 ||
                          currentFilters.timeRange !== TimeRange.THIRTY_DAYS;

  // Extract data from consolidated analytics
  const totalRequestsMonth = dashboardStats?.totalRequests ?? null;
  const overtimeHoursMonth = dashboardStats?.totalHours ?? null;
  const approvalRateMonth = dashboardStats?.totalRequests && dashboardStats.totalRequests > 0 
    ? (dashboardStats.approvedRequests / dashboardStats.totalRequests) * 100 
    : null;
  const pendingRequestsAll = dashboardStats?.pendingRequests ?? null;
  const weekOnWeekTrendMonth = dashboardStats?.weekOnWeekTrend ?? null;

  const departmentComparisonChartRef = useRef<{ download: () => void }>(null);

  // Handle filter changes with debouncing
  const handleFiltersChange = useCallback((newFilters: FilterType) => {
    setCurrentFilters(newFilters);
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Debounce the filter change
    timeoutRef.current = setTimeout(() => {
      startTransition(() => {
        // Filter change is handled by the consolidated analytics hook
        // No additional processing needed here
      });
    }, 300);
  }, []);

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <section className="mb-10">
        {user ? (
          <div className="space-y-6">
            <div className="flex w-full flex-nowrap gap-x-4">
              <div className="w-1/5">
                <StatMetricCard
                  title={<div className="flex items-center gap-1 whitespace-nowrap">Total Requests
                    <TooltipProvider delayDuration={25}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs whitespace-normal px-4 py-2 z-[60]">
                          <div className="font-semibold mb-1">What is this?</div>
                          <div>
                            The total number of overtime requests submitted in the current month, regardless of status.<br /><br />
                            <b>Notes:</b>
                            <ul className="list-disc pl-4">
                              <li>Includes all requests (pending, approved, rejected).</li>
                              <li>Resets at the start of each month.</li>
                            </ul>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>}
                  value={totalRequestsMonth !== null ? totalRequestsMonth : "..."}
                  icon={StatIcon.PRODUCTIVITY}
                  trend={trends?.totalReqTrend ? {
                    value: Number(trends.totalReqTrend.toFixed(2)),
                    label: "vs last month",
                    isPositive: trends.totalReqTrend > 0
                  } : undefined}
                />
              </div>
              
              <div className="w-1/5">
                <StatMetricCard
                  title={<div className="flex items-center gap-1 whitespace-nowrap">Overtime Hours
                    <TooltipProvider delayDuration={25}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs whitespace-normal px-4 py-2 z-[60]">
                          <div className="font-semibold mb-1">What is this?</div>
                          <div>
                            Total approved overtime hours for the current month.<br /><br />
                            <b>Notes:</b>
                            <ul className="list-disc pl-4">
                              <li>Only includes approved requests.</li>
                              <li>Resets at the start of each month.</li>
                            </ul>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>}
                  value={overtimeHoursMonth !== null ? overtimeHoursMonth : "..."}
                  icon={StatIcon.HOURS}
                  trend={trends?.totalHoursTrend ? {
                    value: Number(trends.totalHoursTrend.toFixed(2)),
                    label: "vs last month",
                    isPositive: trends.totalHoursTrend > 0
                  } : undefined}
                />
              </div>
              
              <div className="w-1/5">
                <StatMetricCard
                  title={<div className="flex items-center gap-1 whitespace-nowrap">Approval Rate
                    <TooltipProvider delayDuration={25}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs whitespace-normal px-4 py-2 z-[60]">
                          <div className="font-semibold mb-1">What is this?</div>
                          <div>
                            Percentage of approved requests out of total requests for the current month.<br /><br />
                            <b>Notes:</b>
                            <ul className="list-disc pl-4">
                              <li>Calculated as (Approved / Total) × 100.</li>
                              <li>Resets at the start of each month.</li>
                            </ul>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>}
                  value={approvalRateMonth !== null ? `${approvalRateMonth.toFixed(1)}%` : "..."}
                  icon={StatIcon.APPROVAL}
                  trend={trends?.approvalRateTrend ? {
                    value: Number(trends.approvalRateTrend.toFixed(2)),
                    label: "vs last month",
                    isPositive: trends.approvalRateTrend > 0
                  } : undefined}
                />
              </div>
              
              <div className="w-1/5">
                <StatMetricCard
                  title={<div className="flex items-center gap-1 whitespace-nowrap">Pending Requests
                    <TooltipProvider delayDuration={25}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs whitespace-normal px-4 py-2 z-[60]">
                          <div className="font-semibold mb-1">What is this?</div>
                          <div>
                            Number of pending overtime requests for the current month.<br /><br />
                            <b>Notes:</b>
                            <ul className="list-disc pl-4">
                              <li>Includes pending and pending_vp_approval status.</li>
                              <li>Resets at the start of each month.</li>
                            </ul>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>}
                  value={pendingRequestsAll !== null ? pendingRequestsAll : "..."}
                  icon={StatIcon.ALERT}
                  trend={trends?.pendingTrend ? {
                    value: Number(trends.pendingTrend.toFixed(2)),
                    label: "vs yesterday",
                    isPositive: trends.pendingTrend > 0
                  } : undefined}
                  onClick={canAccessPendingRequests ? handlePendingRequestsClick : undefined}
                />
              </div>
              
              <div className="w-1/5">
                <StatMetricCard
                  title={<div className="flex items-center gap-1 whitespace-nowrap">Weekly Trend
                    <TooltipProvider delayDuration={25}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs whitespace-normal px-4 py-2 z-[60]">
                          <div className="font-semibold mb-1">What is this?</div>
                          <div>
                            Percentage change in approved overtime hours between the last 7 days and the previous 7 days (within the current month).<br /><br />
                            <b>How it works:</b>
                            <ul className="list-disc pl-4">
                              <li>Compares total approved overtime hours in the most recent week to the week before.</li>
                              <li>If last week had 0 hours, trend is shown as 0%.</li>
                              <li>Only approved requests are counted.</li>
                              <li>Large swings can occur if there are very few requests.</li>
                              <li>Trend resets at the start of each month.</li>
                            </ul>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>}
                  value={weekOnWeekTrendMonth !== null ? `${weekOnWeekTrendMonth.toFixed(1)}%` : "..."}
                  icon={StatIcon.APPROVAL}
                  trend={undefined}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Please log in to view your dashboard.</p>
          </div>
        )}
      </section>

      {/* Dashboard Filters - Sticky */}
      <section className="sticky top-0 z-50 mb-8 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
              <DashboardFilters 
                onFiltersChange={handleFiltersChange}
                departments={availableDepartments}
                isLoading={isLoading}
        />
      </section>

      {/* Analytics Charts */}
      {user && (
        <section className="space-y-8">
          {/* Donut Charts Row - All 4 charts in a single row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="h-80">
              <CardContent className="p-4 h-full flex flex-col">
                  <DepartmentComparisonChart
                    ref={departmentComparisonChartRef}
                    data={filteredDepartmentData}
                  title="Overtime by Department"
                  isLoading={isLoading}
                    hasActiveFilters={hasActiveFilters}
                  standalone={true}
                />
              </CardContent>
            </Card>
                
            <Card className="h-80">
              <CardContent className="p-4 h-full flex flex-col">
                  <ApprovalStatusDonutChart
                  requests={chartOvertimeRequests}
                  title="Approval Status"
                  isLoading={isLoading}
                    hasActiveFilters={hasActiveFilters}
                  standalone={true}
                />
                    </CardContent>
                  </Card>

            <Card className="h-80">
              <CardContent className="p-4 h-full flex flex-col">
                  <OvertimeReasonDonutChart
                  requests={chartOvertimeRequests}
                  title="Overtime Reasons"
                  isLoading={isLoading}
                    hasActiveFilters={hasActiveFilters}
                  standalone={true}
                />
                    </CardContent>
                  </Card>

            <Card className="h-80">
              <CardContent className="p-4 h-full flex flex-col">
                  <RejectionReasonDonutChart
                  requests={chartOvertimeRequests}
                  title="Rejection Reasons"
                  isLoading={isLoading}
                    hasActiveFilters={hasActiveFilters}
                  standalone={true}
                  />
              </CardContent>
            </Card>
              </div>

          {/* Charts Row - Department Trends and Overtime Reasons in individual cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <DepartmentTrendlineChart
                  requests={chartOvertimeRequests}
                  title="Department Trends"
                  isLoading={isLoading}
                  filters={currentFilters}
                  standalone={true}
                />
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <OvertimeReasonsChart
                  requests={chartOvertimeRequests}
                  title="Overtime Reasons"
                  isLoading={isLoading}
                  filters={currentFilters}
                  standalone={true}
                />
              </CardContent>
            </Card>
          </div>

          <EmployeeOvertimeTable
            data={chartOvertimeRequests}
            isLoading={isLoading}
          />
        </section>
        )}
    </div>
  );
};

export default Index;